import React, { useState, useEffect, useCallback } from "react";
import { Check, X, Star, Trees, Loader2 } from "lucide-react";
import { useLocation, Link } from "react-router-dom";
import toast from "react-hot-toast";
import camera from "../assets/camera-minimalistic-svgrepo-com.svg";
import bed from "../assets/bed-svgrepo-com.svg";
import ruler from "../assets/ruler-traingle-svgrepo-com (1).svg";
import bath from "../assets/bath-svgrepo-com.svg";
import pool from "../assets/pool-summer-swim-svgrepo-com.svg";
import CarIcon from "../assets/car-side-svgrepo-com.svg";
import fuel from "../assets/fuel-svgrepo-com.svg";
import seats from "../assets/car-seat-with-seatbelt-svgrepo-com.svg";
import driverIcon from "../assets/driver-svgrepo-com.svg";
import capacityIcon from "../assets/users-2-svgrepo-com.svg";
import TypeIcon from "../assets/outdoor-trip-hotel-svgrepo-com.svg";
import buffetIcon from "../assets/food-svgrepo-com.svg";
import colorIcon from "../assets/color-palette-svgrepo-com.svg";
import sizeIcon from "../assets/size-tag-svgrepo-com.svg";
import brandIcon from "../assets/brand-svgrepo-com.svg";
import conditionIcon from "../assets/condition-document-law-svgrepo-com.svg";
import {
  AGENCY_CATEGORIES,
  CATEGORY_LIST,
  CATEGORY_LABELS,
  normalizeCategory,
} from "../constants/agencyCategories";
import {
  approveProduct,
  fetchAllPendingProducts,
  fetchPendingProductsByCategory,
  rejectProduct,
} from "../services/productService";

const tabs = ["All", ...CATEGORY_LIST];

const PendingProducts = () => {
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const selectedFromURL = queryParams.get("selected");

  const [activeTab, setActiveTab] = useState("All");
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actionId, setActionId] = useState(null);

  useEffect(() => {
    if (selectedFromURL) {
      const matchedTab = tabs.find(
        (tab) => tab === "All" || tab === normalizeCategory(selectedFromURL)
      );
      if (matchedTab) setActiveTab(matchedTab);
    }
  }, [selectedFromURL]);

  const loadProducts = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data =
        activeTab === "All"
          ? await fetchAllPendingProducts()
          : await fetchPendingProductsByCategory(activeTab);
      setProducts(data);
    } catch (err) {
      console.error(err);
      setError(err?.friendlyMessage || "Failed to load pending products.");
      setProducts([]);
    } finally {
      setLoading(false);
    }
  }, [activeTab]);

  useEffect(() => {
    loadProducts();
  }, [loadProducts]);

  const filteredProducts =
    activeTab === "All"
      ? products
      : products.filter((p) => normalizeCategory(p.category) === activeTab);

  const handleApprove = async (product) => {
    setActionId(product.id);
    try {
      await toast.promise(approveProduct(product.category, product.id), {
        loading: "Approving...",
        success: "Product approved",
        error: (e) => e?.friendlyMessage || "Approve failed",
      });
      setProducts((prev) => prev.filter((p) => p.id !== product.id));
    } catch (err) {
      console.error(err);
    } finally {
      setActionId(null);
    }
  };

  const handleReject = async (product) => {
    if (!window.confirm(`Reject and remove "${product.name}"?`)) return;
    setActionId(product.id);
    try {
      await toast.promise(rejectProduct(product.category, product.id), {
        loading: "Rejecting...",
        success: "Product rejected",
        error: (e) => e?.friendlyMessage || "Reject failed",
      });
      setProducts((prev) => prev.filter((p) => p.id !== product.id));
    } catch (err) {
      console.error(err);
    } finally {
      setActionId(null);
    }
  };

  return (
    <div className="min-h-screen bg-agarly-light/20 p-8 dark:bg-agarly-dark">
      <div>
        <h1 className="text-2xl font-bold mb-2 dark:text-gray-200">Pending Products</h1>
        <p className="text-agarly-gray pb-6 text-sm dark:text-gray-400">
          <Link to="/dashboard" className="hover:underline">Dashboard</Link>
          {" / "}
          <Link to="/products" className="hover:underline">Categories</Link>
          {" / Pending Products"}
          {activeTab !== "All" && (
            <span className="agarly-gradient-text font-bold">
              {" "}/ {CATEGORY_LABELS[activeTab]}
            </span>
          )}
        </p>
      </div>

      <div className="flex py-1 px-1 md:space-x-2 mb-4 border border-agarly-surface rounded-agarly-lg justify-between gap-1">
        {tabs.map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`py-2 flex-1 text-sm rounded-agarly-md transition-colors ${
              activeTab === tab
                ? "bg-agarly-light text-agarly-dark font-semibold"
                : "text-agarly-gray hover:bg-agarly-light/50 dark:text-gray-300"
            }`}
          >
            {tab === "All" ? "All" : CATEGORY_LABELS[tab]}
          </button>
        ))}
      </div>

      {loading && (
        <div className="flex items-center gap-2 text-agarly-gray py-12">
          <Loader2 className="animate-spin" size={20} />
          Loading pending products...
        </div>
      )}

      {error && !loading && (
        <div className="agarly-card p-4 mb-4 text-agarly-danger">
          {error}
          <button type="button" onClick={loadProducts} className="agarly-btn-secondary ml-4 text-sm">
            Retry
          </button>
        </div>
      )}

      {!loading && !error && (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredProducts.length > 0 ? (
            filteredProducts.map((product) => (
              <ProductCard
                key={product.id}
                product={product}
                busy={actionId === product.id}
                onApprove={() => handleApprove(product)}
                onReject={() => handleReject(product)}
              />
            ))
          ) : (
            <p className="text-agarly-gray dark:text-gray-300 col-span-full">
              No pending products in this category.
            </p>
          )}
        </div>
      )}
    </div>
  );
};

const ProductCard = ({ product, onApprove, onReject, busy }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const images = [product.imageCover, ...(product.images || [])].filter(Boolean);

  return (
    <div className="agarly-card p-4 flex flex-col">
      <div className="relative w-full h-48 overflow-hidden rounded-agarly-lg mb-4 bg-agarly-surface">
        {images.length > 0 ? (
          <img
            src={images[currentIndex]}
            alt={product.name}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-agarly-gray text-sm">
            No image
          </div>
        )}
        {images.length > 1 && (
          <div className="absolute bottom-2 left-0 right-0 flex justify-center gap-2">
            {images.map((_, i) => (
              <button
                key={i}
                type="button"
                onClick={() => setCurrentIndex(i)}
                className={`w-2 h-2 rounded-full ${
                  currentIndex === i ? "bg-agarly-primary" : "bg-agarly-surface"
                }`}
              />
            ))}
          </div>
        )}
      </div>

      <h2 className="text-lg font-semibold text-agarly-dark dark:text-gray-200">{product.name}</h2>
      <p className="text-sm text-agarly-gray">{product.vendor}</p>
      <p className="text-sm text-agarly-gray">{product.location}</p>

      <div className="flex items-center justify-between mt-1">
        <p className="text-xs text-agarly-gray">Added: {product.dateAdded}</p>
        <div className="flex items-center text-agarly-warning">
          <Star size={16} className="fill-current" />
          <span className="text-sm ml-1">{product.rating ?? "—"}</span>
        </div>
      </div>

      <p className="agarly-gradient-text font-bold mt-2 text-lg">{product.rentalFee} EGP</p>
      <p className="text-agarly-gray text-sm mt-2 line-clamp-2">{product.description}</p>

      <div className="mt-3 text-sm text-agarly-gray grid grid-cols-2 gap-2">
        {normalizeCategory(product.category) === AGENCY_CATEGORIES.REAL_ESTATE && (
          <>
            <Feature icon={ruler} label={`Area: ${product.area} m²`} />
            <Feature icon={bed} label={`Bedrooms: ${product.bedrooms}`} />
            <Feature icon={bath} label={`Bathrooms: ${product.bathrooms}`} />
            <Feature icon={camera} label={`Cameras: ${product.cameras ? "Yes" : "No"}`} />
            <Feature icon={pool} label={`Pool: ${product.pool ? "Yes" : "No"}`} />
            <span className="flex items-center">
              <Trees className="inline w-5 h-5 mr-2" /> Garden: {product.garden ? "Yes" : "No"}
            </span>
          </>
        )}
        {normalizeCategory(product.category) === AGENCY_CATEGORIES.CARS && (
          <>
            <Feature icon={CarIcon} label={`${product.brand} ${product.model}`} />
            <Feature icon={fuel} label={`Fuel: ${product.fuel}`} />
            <Feature icon={seats} label={`Seats: ${product.seats}`} />
            <Feature icon={driverIcon} label={`Driver: ${product.driver}`} />
          </>
        )}
        {normalizeCategory(product.category) === AGENCY_CATEGORIES.WEDDING && (
          <>
            <Feature icon={ruler} label={`Area: ${product.area} m²`} />
            <Feature icon={capacityIcon} label={`Capacity: ${product.capacity}`} />
            <Feature icon={TypeIcon} label={`Type: ${product.type}`} />
            <Feature icon={buffetIcon} label={`Buffet: ${product.buffet}`} />
          </>
        )}
        {normalizeCategory(product.category) === AGENCY_CATEGORIES.CLOTHES && (
          <>
            <Feature icon={sizeIcon} label={`Size: ${product.size || "N/A"}`} />
            <Feature icon={colorIcon} label={`Color: ${product.color || "N/A"}`} />
            <Feature icon={brandIcon} label={`Brand: ${product.brand || "N/A"}`} />
            <Feature icon={conditionIcon} label={`Condition: ${product.condition || "N/A"}`} />
          </>
        )}
      </div>

      <div className="mt-4 flex gap-3">
        <button
          type="button"
          disabled={busy}
          onClick={onApprove}
          className="flex-1 flex items-center justify-center gap-2 bg-agarly-success text-white py-2 rounded-agarly-lg hover:opacity-90 disabled:opacity-50"
        >
          <Check size={16} /> Approve
        </button>
        <button
          type="button"
          disabled={busy}
          onClick={onReject}
          className="flex-1 flex items-center justify-center gap-2 bg-agarly-danger text-white py-2 rounded-agarly-lg hover:opacity-90 disabled:opacity-50"
        >
          <X size={16} /> Reject
        </button>
      </div>
    </div>
  );
};

const Feature = ({ icon, label }) => (
  <p className="flex items-center">
    <img src={icon} alt="" className="w-6 h-6 mr-2" />
    {label}
  </p>
);

export default PendingProducts;
